import moment from "moment";
import { FunctionComponent, useEffect, useState } from "react";
import {
  EmailOrdersAuditExportApi,
  GetOrdersApi,
} from "../../../../../service/repos/order_repo";
import { exportCSV } from "../../../../../utils/csv_generator";
import { OrderType } from "../../../../../utils/enums";
import StadiumButton from "../../../../new_components/common/stadium_button";
import jsonexport from "jsonexport";
import { UTCToLocalDate } from "../../../../../utils/date_util";
import { useModal } from "../../../../../context_providers/modal/modal_context";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import Icon from "@mdi/react";
import { mdiDownload, mdiEmailOutline } from "@mdi/js";
import { fetchGet } from "../../../../../service/api_client";

interface ExportWidgetProps {
  exportAllData: boolean;
  approvalFormat?: boolean;
  filter: {
    page?: number;
    search?: string;
    fromDate?: string;
    toDate?: string;
    state?: string[];
    orderStatus?: string[];
    paymentStatus?: string[];
    saleExecutive?: string[];
    type?: string[];
    items?: string[];
    site_survey?: string[];
    wiring_status?: string[];
    subsidy_status?: string[];
    approval_status?: string;
  };
}

// Exact column order for the "Export for Approval" file. approval_action,
// Selected Warehouse, Suggested Transport and Remarks are the reviewer-filled
// columns (left blank on export). Suggested Transport and Remarks are optional.
const APPROVAL_HEADERS = [
  "order_date",
  "order_id",
  "type",
  "Sales type",
  "category",
  "customer",
  "customer group",
  "poc",
  "mobile",
  "email",
  "city",
  "state",
  "registered_pincode",
  "status",
  "payment_status",
  "approval_status",
  "relationship_manager",
  "sales_emp",
  "GST Number",
  "Bill To Address",
  "Ship To Address",
  "Category (Products/Services/Raw Materials)",
  "item code",
  "product",
  "price",
  "qty",
  "total_amt",
  "approval_action",
  "Selected Warehouse",
  "Suggested Transport",
  "Remarks",
];

const formatAddr = (a: any): string => {
  if (!a) return "";
  return [a.line1, a.line2, a.line3, a.city, a.state, a.pincode]
    .filter((x) => x !== undefined && x !== null && String(x).trim() !== "")
    .join(", ");
};

const ExportWidget: FunctionComponent<ExportWidgetProps> = (props) => {
  const { setLoading } = useModal();
  const { showToast } = useToast();

  const [fromDate, setFromdate] = useState("");
  const [toDate, setTodate] = useState("");
  const [isEmailingDeviceExport, setIsEmailingDeviceExport] = useState(false);
  const [isEmailingOrdersExport, setIsEmailingOrdersExport] = useState(false);

  // "For approval" format: leaner columns + GST/addresses/item-code/sales-type,
  // and blank approval_action + Selected Warehouse (plus optional Suggested
  // Transport + Remarks) for the reviewer. All line
  // items of an order stay together (one row per item, order fields repeated).
  const getApprovalExpObj = (orders: any[]) => {
    const rows: any[] = [];

    orders.forEach((order) => {
      const cust = order.customer ?? {};
      const base: Record<string, any> = {
        order_date: order.order_date
          ? moment(order.order_date).format("DD-MM-YYYY hh:mm:ss a")
          : "",
        order_id: order.order_id,
        type: order.type,
        "Sales type": order.sale_type ?? "",
        category: order.category ?? "",
        customer:
          (cust.first_name ?? cust.firstName ?? "") +
          " " +
          (cust.lastName ?? cust.last_name ?? ""),
        "customer group":
          cust.additional_customer_details?.customer_group ?? "",
        poc:
          order.type === OrderType.B2B || order.type === OrderType.PARTNER
            ? order.poc
            : "N/A",
        mobile: cust.mobile ?? "",
        email: cust.email ?? "",
        city: cust.address?.city ?? "",
        state: cust.address?.state ?? "",
        registered_pincode: cust.address?.pincode ?? "",
        status: order.status,
        payment_status: order.payment_status,
        approval_status:
          order.approval_status === "Approved" ? "Approved" : "Unapproved",
        relationship_manager: order.rm_name ?? "",
        sales_emp: order.sales_person_name ?? "",
        "GST Number": cust.gst ?? cust.gst_details?.gst_number ?? "",
        "Bill To Address": formatAddr(
          order.billing_profile?.address ?? cust.billing_address?.[0],
        ),
        "Ship To Address": formatAddr(
          order.delivery_profile?.address ??
            order.destination_address ??
            cust.delivery_address?.[0],
        ),
      };

      const lineItems = [
        ...(Array.isArray(order.products) ? order.products : []).map(
          (p: any) => ({ item: p, group: "Products" }),
        ),
        ...(Array.isArray(order.services) ? order.services : []).map(
          (p: any) => ({ item: p, group: "Services" }),
        ),
        ...(Array.isArray(order.raw_materials) ? order.raw_materials : []).map(
          (p: any) => ({ item: p, group: "Raw Materials" }),
        ),
      ];

      const blanks = {
        approval_action: "",
        "Selected Warehouse": "",
        "Suggested Transport": "",
        Remarks: "",
      };

      if (!lineItems.length) {
        rows.push({
          ...base,
          "Category (Products/Services/Raw Materials)": "",
          "item code": "",
          product: "",
          price: "",
          qty: "",
          total_amt: "",
          ...blanks,
        });
        return;
      }

      lineItems.forEach(({ item: p, group }) => {
        rows.push({
          ...base,
          "Category (Products/Services/Raw Materials)": group,
          "item code": p.item?.item_code ?? "",
          product: p.item?.name ?? "",
          price: p.price ?? "",
          qty: p.qty ?? "",
          total_amt: p.total ?? 0,
          ...blanks,
        });
      });
    });

    return rows;
  };

  // Direct download — kept for the "for approval" format only. The regular
  // orders export is email-only (see emailOrdersAuditExport below): a
  // full-quarter CSV streamed directly hits the same API Gateway
  // body-size/timeout ceiling as the device-allocation export.
  const getOrders = async (customRange?: { from: string; to: string }) => {
    const startDate = customRange?.from || fromDate;
    const endDate = customRange?.to || toDate;

    if (!startDate || !endDate) {
      showToast({ type: ToastType.warning, text: "Please select date!" });
      return;
    }

    if (
      moment(startDate, "YYYY-MM-DD").isAfter(moment(endDate, "YYYY-MM-DD"))
    ) {
      showToast({
        type: ToastType.warning,
        text: "From Date should not be greater than To Date",
      });
      return;
    }

    setLoading(true);

    const { search, ...restFilter } = props.filter;

    const res = await GetOrdersApi({
      ...restFilter,
      fromDate: UTCToLocalDate(startDate) as any,
      toDate: UTCToLocalDate(endDate) as any,
      all: "true",
    });

    if (res.success) {
      const exportData = getApprovalExpObj(res.data.orders);
      jsonexport(exportData, { headers: APPROVAL_HEADERS }, (err, csv) => {
        if (err) return console.error(err);
        exportCSV(csv, `${startDate}-${endDate}-orders-for-approval`);
      });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }

    setLoading(false);
  };

  // Regular orders export (ERP-326): queues the grouped audit export
  // server-side and emails the signed-in user an S3 link, same UX as the
  // existing "Device allocated data (emailed)" export below.
  const emailOrdersAuditExport = async (customRange?: {
    from: string;
    to: string;
  }) => {
    const startDate = customRange?.from || fromDate;
    const endDate = customRange?.to || toDate;

    if (!startDate || !endDate) {
      showToast({ type: ToastType.warning, text: "Please select date!" });
      return;
    }

    if (
      moment(startDate, "YYYY-MM-DD").isAfter(moment(endDate, "YYYY-MM-DD"))
    ) {
      showToast({
        type: ToastType.warning,
        text: "From Date should not be greater than To Date",
      });
      return;
    }

    setIsEmailingOrdersExport(true);

    const { search, ...restFilter } = props.filter;

    const res = await EmailOrdersAuditExportApi({
      ...restFilter,
      fromDate: UTCToLocalDate(startDate) as any,
      toDate: UTCToLocalDate(endDate) as any,
    });

    if (res.success) {
      showToast({
        type: ToastType.success,
        text: res.message ?? "Export will be emailed to you shortly.",
      });
    } else {
      showToast({
        type: ToastType.error,
        text: res.error ?? "Could not queue the export.",
      });
    }

    setIsEmailingOrdersExport(false);
  };

  useEffect(() => {
    if (props.filter.fromDate && props.filter.toDate) {
      setFromdate(props.filter.fromDate);
      setTodate(props.filter.toDate);
    }
  }, [props.filter.fromDate, props.filter.toDate]);

  const emailDeviceAllocationExport = async () => {
    setIsEmailingDeviceExport(true);
    try {
      const res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/orders/report/anokhi-report/device-allocation/email`,
      );
      if (res.success) {
        showToast({
          type: ToastType.success,
          text: res.message ?? "Export will be emailed to you shortly.",
        });
      } else {
        showToast({
          type: ToastType.error,
          text: res.error ?? "Could not queue the export.",
        });
      }
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: error?.message ?? "Could not queue the export.",
      });
    }
    setIsEmailingDeviceExport(false);
  };

  return (
    <div className="p-5 text-sm">
      <div className="flex flex-col gap-1 my-2">
        <button
          type="button"
          disabled={isEmailingDeviceExport}
          onClick={emailDeviceAllocationExport}
          className="flex items-center gap-1 disabled:opacity-50"
        >
          <Icon path={mdiEmailOutline} className="h-4 w-5"></Icon>
          <p>
            {isEmailingDeviceExport
              ? " Requesting…"
              : " Device allocated data (emailed)"}
          </p>
        </button>
      </div>

      {props.exportAllData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label htmlFor="from-date">From</label>
              <input
                id="from-date"
                className="border rounded p-2 w-full"
                type="date"
                value={fromDate}
                required
                onChange={(e) => {
                  const val = e.target.value;
                  if (toDate && moment(val).isAfter(moment(toDate))) {
                    showToast({
                      type: ToastType.warning,
                      text: "From Date should not be greater than To Date",
                    });
                    return;
                  }
                  setFromdate(val);
                }}
              />
            </div>

            <div>
              <label htmlFor="to-date">To</label>
              <input
                id="to-date"
                className="border rounded p-2 w-full"
                type="date"
                value={toDate}
                required
                min={fromDate || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (fromDate && moment(fromDate).isAfter(moment(val))) {
                    showToast({
                      type: ToastType.warning,
                      text: "To Date should be greater than From Date",
                    });
                    return;
                  }
                  setTodate(val);
                }}
              />
            </div>
          </div>

          <div className="flex justify-end my-2 gap-2">
            {props.approvalFormat ? (
              <StadiumButton type="button" onTap={getOrders}>
                <Icon path={mdiDownload} size={0.8} className="mr-1" />
                Export
              </StadiumButton>
            ) : (
              <button
                type="button"
                disabled={isEmailingOrdersExport}
                onClick={() => emailOrdersAuditExport()}
                className="flex items-center gap-1 disabled:opacity-50 px-6 py-2 rounded border hover:shadow text-sm font-semibold"
              >
                <Icon path={mdiEmailOutline} size={0.8} className="mr-1" />
                {isEmailingOrdersExport ? "Requesting…" : "Email this export"}
              </button>
            )}
          </div>
          {!props.approvalFormat && (
            <p className="text-xs text-gray-500 text-right mt-1">
              We'll email you a download link as soon as it's ready — you can
              close this window and carry on.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default ExportWidget;
